import { v4 as uuidv4 } from 'uuid';

export const generatePipelineYaml = (inputText) => {
  const uuids = {
    defineText: { id: uuidv4(), output: uuidv4() },
    queryPubMed: { id: uuidv4(), output: uuidv4() },
    queryPubTator: { id: uuidv4(), output: uuidv4() },
    pubCredibility: { id: uuidv4(), output: uuidv4() },
    topicClustering: { id: uuidv4(), output: uuidv4() },
    tableJoin: { id: uuidv4(), output: uuidv4() },
    importData: { id: uuidv4() },
    normalizeGeneNames: { id: uuidv4(), output: uuidv4() },
  };

  const result = `components:
  - id: ${uuids.defineText.id}
    componentType: DefineText
    name: Define Disease / Gene
    inputs: {}
    extraParams: []
    outputs:
      result: ${uuids.defineText.output}
    params:
      text: ${inputText}
  - id: ${uuids.queryPubMed.id}
    componentType: QueryPubMed
    name: Query PubMed
    inputs: {}
    extraParams:
      - ${uuids.defineText.output}
    outputs:
      result: ${uuids.queryPubMed.output}
    params:
      query: $param1
      generateQuery: true
      max_results: 250
  - id: ${uuids.queryPubTator.id}
    componentType: QueryPubTator
    name: Query PubTator
    inputs:
      sourceTable: ${uuids.queryPubMed.output}
    extraParams: []
    outputs:
      result: ${uuids.queryPubTator.output}
    params: {}
  - id: ${uuids.pubCredibility.id}
    componentType: PublicationCredibility
    name: Publication Credibility
    inputs:
      sourceTable: ${uuids.queryPubTator.output}
    extraParams: []
    outputs:
      result: ${uuids.pubCredibility.output}
    params: {}
  - id: ${uuids.topicClustering.id}
    componentType: TopicClustering
    name: Topic Clustering
    inputs:
      sourceTable: ${uuids.queryPubTator.output}
    extraParams: []
    outputs:
      result: ${uuids.topicClustering.output}
    params: {}
  - id: ${uuids.tableJoin.id}
    componentType: TableJoin
    name: Join Tables
    inputs:
      sourceTable1: ${uuids.topicClustering.output}
      sourceTable2: ${uuids.pubCredibility.output}
    extraParams: []
    outputs:
      result: ${uuids.tableJoin.output}
    params:
      matchColumn1: id
      matchColumn2: id
      joinType: left
      columnsTable1:
        - topics
        - umap_x
        - umap_y
      columnsTable2:
        - title
        - authors
        - publication_year
        - journal_name
        - abstract
        - mesh_terms
        - publication_type
        - mentioned_genes
        - mentioned_variants
        - mentioned_diseases
        - mentioned_chemicals
        - mentioned_species
        - citations
        - jcr_journal_impact_factor
        - scimago_journal_rank
        - credibility
  - id: ${uuids.importData.id}
    componentType: ImportDataContext
    name: Import in Bio|Verse
    inputs:
      sourceTable: ${uuids.normalizeGeneNames.output}
    extraParams: []
    outputs: {}
    params:
      entityType: etl_publications
      datatableId: publications_data_table
  - id: ${uuids.normalizeGeneNames.id}
    componentType: NormalizeGeneNames
    name: Normalize Gene Names
    inputs:
      sourceTable: ${uuids.tableJoin.output}
    extraParams: []
    outputs:
      result: ${uuids.normalizeGeneNames.output}
    params:
      geneNamesInput: mentioned_genes
`;
  return result;
};

export const generateDashboardYaml = () => {
  const dashboardYaml = `
templateContent:
  assetType: bioreflect_pipeline
  displayName: Bio|verse Dashboard for bioreflect_results
  elements:
    sections:
    - elements:
      - content: A summary of literature fetched from Pubmed. It includes an overview of each paper information, credibility,
          gene mentions extracted via Pubtator, and citation counts regarding base on a Pubmed Query.
        type: text
      - elements:
        - table_bioreflect_publications
        type: layoutHorizontal
      - elements:
        - widgetPaper_gene
        - widgetPaper_disease
        - widgetPaper_authors
        type: layoutHorizontal
      - elements:
        - widgetPaper_year
        - widgetPaper_cred_score
        - widgetPaper_citations
        type: layoutHorizontal
      id: sc1
      title: Pubmed Literature Summary
    - elements:
      - content: A topic modelling visual overview of the paper content, with a content
          promixity based visual embedding.
        type: text
      - elements:
        - facet_topic
        - embedding_umap
        type: layoutHorizontal
      id: sc2
      title: Paper topic modelling
    
    widgets:
      table_bioreflect_publications:
        chartType: DataTable
        config:
          columns:
          - id: id
            width: 100
          - bold: true
            id: title
            width: 450
          - id: publication_year
            width: 75
          - id: journal_name
            width: 100
          - id: citations
            numericalBar:
              maxVal: 150
              minVal: 0
            width: 75
          - id: scimago_journal_rank
            numericalBar:
              maxVal: 146
              minVal: 0
            width: 75
          - id: jcr_journal_impact_factor
            numericalBar:
              maxVal: 50
              minVal: 0
            width: 75
          - id: authors
            useBadge: true
            width: 400
          - id: mentioned_diseases
            useBadge: true
            width: 400
          - id: mentioned_genes_normalized
            useBadge: true
            width: 400
        dataBinding:
          aspectBindings: {}
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 500

      widgetPaper_year:
        chartType: Histogram
        dataBinding:
          dataTableId: publications_data_table
          aspectBindings:
            value: publication_year
        config:
          minVal: 1990
          maxVal: 2025
          fixedBinSize: 1
        layoutProperties:
          fraction: 1
          height: 350
  

      widgetPaper_citations:
        chartType: Histogram
        config:
          maxVal: 150
          minVal: 0
          resolution: 10
        dataBinding:
          aspectBindings:
            value: citations
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      widgetPaper_cred_score:
        chartType: Histogram
        config: {}
        dataBinding:
          aspectBindings:
            value: credibility
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350
      
      widgetPaper_disease:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: mentioned_diseases
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      widgetPaper_gene:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: mentioned_genes_normalized
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      widgetPaper_authors:
        chartType: FacetCategorical
        config:
          simpleLayout: false
        dataBinding:
          aspectBindings:
            categories: authors
          dataTableId: publications_data_table
        layoutProperties:
          fraction: 1
          height: 350

      embedding_umap:
          chartType: ScatterPlot
          dataBinding:
            aspectBindings:
              color: topics
              hoverLabel: journal_name
              x: umap_x
              y: umap_y
            dataTableId: publications_data_table
          config:
            opacity: 0.5
            hideAxes: true
            hideColorLegend: true
            pointSizeFactor: 1.25
            colorSchemeCategorical: "Default 20"
          layoutProperties:
            fraction: 0.6
            height: 600
          title: UMAP Visualization of Research Papers 

      facet_topic:
        chartType: ColorLegendCategorical
        dataBinding:
          aspectBindings:
            categories: topics
          dataTableId: publications_data_table
        config:
          categoriesSchemeCategorical: "Default 20"
        layoutProperties:
          fraction: 0.4
          height: 600
        title: Research Topics


  templateId: default
  version: 0.1.0
templateScope: dashboard
templateType: bioreflect_pipeline

`;

  return dashboardYaml;
};

export const generateEntityYaml = () => {
  return `
templateContent:
  assetType: bioreflect_pipeline
  displayName: Bio|verse Entity for bioreflect_results
  entities:
  - displayName: Publication
    elements:
    - title: Properties
      type: propertyList
    entityType: etl_publications
  templateId: default
  version: 0.1.0
templateScope: entity
templateType: bioreflect_pipeline
`;
};
